<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import type { FieldSchema } from '../../../types';

const props = withDefaults(
  defineProps<{
    disabled?: boolean;
    error?: string;
    field?: FieldSchema;
    placeholder?: string;
    required?: boolean;
  }>(),
  {
    placeholder: 'components.forms.placeholder.select',
  }
);

const { t } = useI18n();

const model = defineModel<{ name: string; type: string; value: string }[]>({
  default: [],
});

const _model = ref();

const activeModelProp = computed(() => props?.field?.model);
const options = computed(() => [t('common.positive'), t('common.negative'), t('common.unknown')]);
const optionsType = computed(() => [t('common.select'), t('common.text')]);

onMounted(() => initModel());

watch(
  () => props.field?.settings?.scheduling,
  () => updateModel(),
  { immediate: true }
);
watch(activeModelProp, (newValue) => {
  if (newValue && newValue?.length) _model.value = newValue;
  updateModel();
});

function initModel() {
  if (!model.value || !model.value?.length) {
    _model.value = [{ name: t('components.forms.button.result1'), type: 'select' }];

    model.value = _model.value;
    return;
  }

  _model.value = model.value;
  updateModel();
}

function updateModel() {
  if (props.field?.settings?.allowModelFill) return (_model.value = model.value);
  if (props.field?.settings?.scheduling) return (model.value = _model.value);

  const hasValid = _model?.value?.every(
    (entry: any) => entry.value !== null && entry.value !== undefined && entry.value !== ''
  );

  model.value = hasValid ? _model.value : null;
}

function onNewInput(type: 'select' | 'text') {
  _model.value.push({
    name: t('components.forms.labels.result', { index: _model.value?.length + 1 }),
    type: type.toLowerCase(),
  });

  model.value = _model.value;
  updateModel();
}

function onRemoveInput(index: number) {
  if (_model.value?.length <= 1) return;

  _model.value.splice(index, 1);

  model.value = _model.value;
  updateModel();
}
</script>

<template>
  <div class="mb-0.5 mt-2 flex text-xs font-medium">
    <p :class="['w-full max-w-[115px]', error ? 'text-red-500' : 'text-gray-500']">
      {{ $t('common.label') }}
    </p>

    <p :class="['w-full max-w-9', error ? 'text-red-500' : 'text-gray-500']">
      {{ $t('common.type') }}
    </p>

    <p :class="[error ? 'text-red-500' : 'text-gray-500']">
      {{ $t('common.results') }}
    </p>
  </div>

  <div
    v-for="(entry, index) in _model"
    class="mb-2"
    :key="index"
  >
    <InputGroup>
      <InputGroupAddon class="h-9 max-w-[110px] p-0">
        <AFieldInputText
          v-model="_model[index].name"
          class="rounded-r-none text-sm"
          placeholder="components.forms.placeholder.addLabel"
          :error
          :disabled="field?.state.disabled || disabled"
          :required="field?.settings?.required || required"
          @change="updateModel"
        />
      </InputGroupAddon>

      <Dropdown
        v-model="_model[index].type"
        pt:dropdownIcon="!hidden"
        pt:filterInput="h-7 py-1 text-sm"
        pt:input="hidden"
        pt:itemLabel="text-sm"
        pt:wrapper="!max-h-none"
        :error
        :class="[
          'flex h-9 w-16 max-w-16 items-center justify-center border-x-0',
          error ? 'border-red-500' : 'hover:bg-sky-100 active:bg-sky-200',
        ]"
        :disabled="field?.state.disabled || disabled"
        :options="optionsType"
        :placeholder="$t('common.type')"
        :pt:trigger="['!p-0 pi pi-pencil', error ? 'text-red-500' : 'text-sky-500']"
        :required="field?.settings?.required || required"
        @change="updateModel"
      />

      <AFieldDropdown
        v-if="_model[index].type?.toLowerCase() === 'select'"
        v-model="_model[index].value"
        :error
        :options
        :disabled="field?.state.disabled || disabled"
        :placeholder="field?.settings?.placeholder || placeholder"
        :required="field?.settings?.required || required"
        @change="updateModel"
      />

      <AFieldInputText
        v-else
        v-model="_model[index].value"
        isResultsField
        :error
        :options
        :disabled="field?.state.disabled || disabled"
        :placeholder="$t('components.forms.placeholder.common')"
        :required="field?.settings?.required || required"
        @change="updateModel"
      />

      <InputGroupAddon :class="['h-9 p-0 ', error ? 'border-red-500' : 'border']">
        <Button
          outlined
          class="h-fit w-fit border-none px-2 py-1"
          icon="pi pi-trash text-sm"
          severity="danger"
          :error
          :disabled="model?.length === 1 || field?.state.disabled || disabled"
          @click="onRemoveInput(index)"
        />
      </InputGroupAddon>
    </InputGroup>
  </div>

  <Button
    v-if="!field?.settings.noButton"
    outlined
    class="af-button w-fit text-sm"
    icon="pi pi-plus text-sm"
    severity="info"
    :disabled="field?.state.disabled || disabled"
    :label="$t('components.forms.button.result')"
    @click="onNewInput('select')"
  />
</template>
